import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, RefreshCw, Plus, Layers, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import PanelFiltros from "@/components/reparto/PanelFiltros";
import ResumenAnalisis from "@/components/reparto/ResumenAnalisis";
import DiaRutaSelector from "@/components/reparto/DiaRutaSelector";
import GrupoCard from "@/components/reparto/GrupoCard";
import MapaPropuesta from "@/components/reparto/MapaPropuesta";
import CrearTodasDialog from "@/components/reparto/CrearTodasDialog";
import OportunidadesDialog from "@/components/reparto/OportunidadesDialog";
import SeleccionManualBanner from "@/components/reparto/SeleccionManualBanner";
import FechasRutaDialog, { paradasConFechaDistinta } from "@/components/reparto/FechasRutaDialog";
import { KEY_REPARTO_SEL } from "@/components/calendario/seleccionRuta";
import {
  moverParada, excluirParada, restaurarParada, toggleFijar, limpiarVacios, anadirGrupoVacio, recomputeGrupo, resumenCreacion,
  sanearGrupos, dedupeLineaIds, anadirParadas,
} from "@/lib/repartoPropuesta";

const FILTROS_INICIALES = {
  fecha_inicio: "", fecha_fin: "", incluir_atrasados: false, incluir_sin_fecha: false,
  fecha_ruta: "", alcance: "periodo",
  usar_fecha_objetivo: true, solo_material_listo: true,
  provincias: [], ciudades: [], peticion_ids: [],
  max_paradas: 25, n_grupos: "",
  inicio: { lat: "37.5772", lon: "-5.9755", preset: "nave" },
};

const aPayload = (f) => {
  const p = {
    fecha_inicio: f.fecha_inicio || undefined, fecha_fin: f.fecha_fin || f.fecha_inicio || undefined,
    incluir_atrasados: f.incluir_atrasados, incluir_sin_fecha: f.incluir_sin_fecha,
    fecha_ruta: f.fecha_ruta || f.fecha_inicio || undefined, alcance: f.alcance,
    usar_fecha_objetivo: f.usar_fecha_objetivo,
    solo_material_listo: f.solo_material_listo, provincias: f.provincias, ciudades: f.ciudades, peticion_ids: f.peticion_ids,
    max_paradas: f.max_paradas === "" ? null : Number(f.max_paradas),
    n_grupos: f.n_grupos === "" ? null : Number(f.n_grupos),
  };
  if (f.inicio.lat !== "" && f.inicio.lon !== "") { p.inicio_lat = Number(f.inicio.lat); p.inicio_lon = Number(f.inicio.lon); }
  return p;
};

/** Reparto inteligente: planificador por capacidad + geografía + elegibilidad, con borrador editable. */
export default function RepartoInteligente() {
  const location = useLocation();
  const nav = useNavigate();
  // Modo «selección manual desde el calendario»: líneas exactas precargadas, sin PanelFiltros ni Analizar
  const [manual, setManual] = useState(() => {
    const st = location.state?.seleccion;
    if (st?.linea_ids?.length) return st;
    try { const s = JSON.parse(sessionStorage.getItem(KEY_REPARTO_SEL) || "null"); return s?.linea_ids?.length ? s : null; } catch { return null; }
  });
  const [manualInfo, setManualInfo] = useState(null);   // {no_elegibles, fechas}
  const manualCargadoRef = React.useRef(false);
  const [fechasOpen, setFechasOpen] = useState(false);
  const [opciones, setOpciones] = useState(null);
  const [f, setF] = useState(FILTROS_INICIALES);
  const [analisis, setAnalisis] = useState(null);
  const [grupos, setGrupos] = useState(null);       // borrador editable (estado local)
  const [excluidas, setExcluidas] = useState([]);
  const [historial, setHistorial] = useState([]);   // deshacer (último cambio)
  const [montadores, setMontadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const [crearRes, setCrearRes] = useState(null);
  const [idemKey, setIdemKey] = useState(null);   // clave de idempotencia de "Crear todas" (doble clic / reintento)
  const creandoRef = React.useRef(false);
  const [lineasEnrutadas, setLineasEnrutadas] = useState([]);   // líneas ya creadas en ruta en esta sesión (para limpiar la selección del calendario)
  const inicio = f.inicio.lat !== "" && f.inicio.lon !== "" ? { lat: Number(f.inicio.lat), lon: Number(f.inicio.lon) } : null;
  const fechaRuta = f.fecha_ruta || f.fecha_inicio;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const cargarSeleccion = async (sel) => {
    setLoading(true); setGrupos(null); setExcluidas([]); setHistorial([]); setCrearRes(null); setIdemKey(null);
    setF((prev) => ({ ...prev, fecha_inicio: sel.fecha_ruta, fecha_fin: sel.fecha_ruta, fecha_ruta: sel.fecha_ruta, max_paradas: "" }));
    try {
      const { data } = await api.post("/reparto-inteligente/desde-seleccion", { linea_ids: sel.linea_ids, fecha_ruta: sel.fecha_ruta, inicio_lat: inicio?.lat, inicio_lon: inicio?.lon });
      setAnalisis({ resumen: data.resumen, excluidos_detalle: data.excluidos_detalle });
      setManualInfo({ no_elegibles: data.no_elegibles, fechas: data.fechas });
      const san = sanearGrupos(data.grupos.map((g) => ({ ...g, montador: null })), inicio);
      setGrupos(san.grupos);
      if (!data.grupos.length) toast.error(data.msg || "Ninguna línea elegible");
      else toast.success(`Selección del calendario: ${data.resumen.candidatos} trabajo(s) · ${data.resumen.n_destinos} destino(s) · ruta del ${sel.fecha_ruta}`);
      if (data.no_elegibles?.length) toast.warning(`${data.no_elegibles.length} línea(s) de la selección no se pueden rutar (ver detalle)`, { duration: 8000 });
    } catch (err) { toast.error(err.response?.data?.detail?.msg || err.response?.data?.detail || "No se pudo cargar la selección"); }
    finally { setLoading(false); }
  };
  const volverAlCalendario = (lineasEnrutadas = []) => {
    sessionStorage.removeItem(KEY_REPARTO_SEL);
    nav("/calendario", { state: { desdeReparto: true, lineasEnrutadas } });
  };

  useEffect(() => {
    api.get("/reparto-inteligente/opciones").then(({ data }) => {
      setOpciones(data);
      if (manual) return;
      setF((prev) => ({ ...prev, fecha_inicio: prev.fecha_inicio || data.fecha_inicio_default, fecha_fin: prev.fecha_fin || data.fecha_fin_default,
        fecha_ruta: prev.fecha_ruta || data.fecha_inicio_default }));
    }).catch(() => toast.error("No se pudieron cargar las opciones del planificador"));
    if (manual && !manualCargadoRef.current) { manualCargadoRef.current = true; cargarSeleccion(manual); }
    Promise.all([api.get("/users"), api.get("/colaboradores").catch(() => ({ data: [] }))]).then(([u, c]) => {
      const internos = u.data.filter((x) => x.active !== false && (x.roles || []).includes("montador"))
        .map((x) => ({ value: `u:${x.id}`, label: x.name || x.email, montador: { usuario_id: x.id, nombre_visible: x.name || x.email } }));
      const externos = c.data.filter((x) => x.activo !== false)
        .map((x) => ({ value: `e:${x.id}`, label: `${x.nombre} (externo)`, montador: { externo_id: x.id, nombre_visible: x.nombre } }));
      setMontadores([...internos, ...externos]);
    }).catch(() => {});
  }, []);

  const aplicar = (nuevosGrupos, nuevasExcluidas) => {
    setHistorial((h) => [...h.slice(-19), { grupos, excluidas }]);
    if (nuevosGrupos) setGrupos(nuevosGrupos);
    if (nuevasExcluidas) setExcluidas(nuevasExcluidas);
  };
  const deshacer = () => {
    const last = historial[historial.length - 1];
    if (!last) return;
    setGrupos(last.grupos); setExcluidas(last.excluidas); setHistorial((h) => h.slice(0, -1));
  };

  const analizar = async () => {
    setLoading(true); setGrupos(null); setExcluidas([]); setHistorial([]);
    try {
      const { data } = await api.post("/reparto-inteligente/analizar", { ...aPayload(f), alcance: "periodo" });
      setAnalisis(data);
      const r = data.resumen;
      if (r.candidatos === 0) toast.warning("Ningún candidato con los filtros actuales. Revisa los excluidos.");
      else toast.success(`${r.candidatos} trabajos · ${r.n_destinos} destinos → ${r.k_generar} ruta(s) (máx ${r.max_paradas ?? "∞"} paradas)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error analizando"); }
    finally { setLoading(false); }
  };

  const generar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reparto-inteligente", aPayload(f));
      setAnalisis({ resumen: data.resumen, excluidos_detalle: data.excluidos_detalle });
      const san = sanearGrupos(data.grupos.map((g) => ({ ...g, montador: null })), inicio);
      if (san.repetidas) toast.warning(`Se han corregido ${san.repetidas} parada(s) repetida(s) en la propuesta (misma línea más de una vez)`, { duration: 9000 });
      setGrupos(san.grupos);
      setExcluidas([]); setHistorial([]); setCrearRes(null); setIdemKey(null);
      if (!data.grupos.length) toast.error(data.msg || "Sin candidatos");
      else toast.success(`✨ ${data.grupos.length} ruta(s) sugeridas (${data.total_con_geo} paradas) · edita el borrador antes de crear`);
      if (data.resumen?.aviso_capacidad) toast.warning(data.resumen.aviso_capacidad, { duration: 8000 });
      (data.grupos[0]?.avisos_globales || []).forEach((a) => toast.warning(a, { duration: 8000 }));
    } catch (err) { toast.error(err.response?.data?.detail || "Error generando"); }
    finally { setLoading(false); }
  };

  // ---- edición del borrador (sólo estado local) ----
  const onMover = (lineaId, destinoId) => {
    const r = moverParada(grupos, lineaId, destinoId, inicio);
    if (r.error) { toast.error(r.error); return; }
    aplicar(r.grupos, null);
  };
  const onFijar = (lineaId) => aplicar(toggleFijar(grupos, lineaId), null);
  const onExcluir = (lineaId, motivo) => {
    const r = excluirParada(grupos, excluidas, lineaId, motivo, inicio);
    aplicar(r.grupos, r.excluidas);
    toast(`Parada excluida de esta propuesta (${motivo}) · la línea sigue pendiente en la app`);
  };
  const onRestaurar = (lineaId, destinoId) => {
    const r = restaurarParada(grupos, excluidas, lineaId, destinoId, inicio);
    if (r.error) { toast.error(r.error); return; }
    aplicar(r.grupos, r.excluidas);
  };
  const onMontador = (grupoId, m) => setGrupos((gs) => gs.map((g) => g.id === grupoId ? { ...g, montador: m ? m.montador : null } : g));
  const [oportunidadesId, setOportunidadesId] = useState(null);   // ruta del borrador con el diálogo de oportunidades abierto
  const onAnadirOportunidades = (grupoId, paradas) => {
    const r = anadirParadas(grupos, grupoId, paradas, inicio);
    if (!r.error) aplicar(r.grupos, null);
    return r;
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || !active) return;
    if (active.data.current?.grupoId === over.id) return;
    onMover(String(active.id), String(over.id));
  };

  const reoptimizar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reparto-inteligente/reoptimizar", {
        grupos: grupos.map((g) => ({ id: g.id, paradas: g.paradas, montador: g.montador })),
        excluidas: excluidas.map((p) => p.linea_id),
        max_paradas: f.max_paradas === "" ? null : Number(f.max_paradas),
        inicio_lat: inicio?.lat, inicio_lon: inicio?.lon, fecha_plan: fechaRuta,
      });
      const san = sanearGrupos(data.grupos.map((g) => recomputeGrupo(g, inicio)), inicio);
      if (san.repetidas) toast.warning(`Se han corregido ${san.repetidas} parada(s) repetida(s)`, { duration: 8000 });
      aplicar(san.grupos, null);
      toast.success("Propuesta reoptimizada respetando fijadas y excluidas");
      (data.avisos || []).forEach((a) => toast.warning(a, { duration: 7000 }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reoptimizar; se conserva la propuesta actual", { duration: 9000 });
    } finally { setLoading(false); }
  };

  const crearTodas = async ({ estado, prefijo }) => {
    if (creandoRef.current) return;            // doble clic
    creandoRef.current = true;
    setLoading(true);
    try {
      const san = sanearGrupos(grupos, inicio);
      if (san.repetidas) { toast.warning(`Se han corregido ${san.repetidas} parada(s) repetida(s) antes de crear`); setGrupos(san.grupos); }
      const key = idemKey || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      setIdemKey(key);
      const { data } = await api.post("/reparto-inteligente/crear-rutas", {
        fecha: fechaRuta, estado, prefijo_nombre: prefijo, idempotency_key: key,
        rutas: san.grupos.filter((g) => g.paradas.length).map((g) => ({
          id: g.id, nombre: `${prefijo} ${fechaRuta} · ${g.ciudad_principal || g.id}`,
          linea_ids: dedupeLineaIds(g.paradas.map((p) => p.linea_id)), montador: g.montador,
        })),
      });
      if (data.repetida) toast.info("Esta creación ya se había ejecutado; se muestra el resultado anterior sin crear nada nuevo");
      setCrearRes(data);
      if (data.fallidas === 0) toast.success(`✅ ${data.creadas} ruta(s) creadas`);
      else toast.warning(`${data.creadas} creadas · ${data.fallidas} fallidas — revisa el detalle`, { duration: 9000 });
      if (data.creadas > 0) {
        // Las creadas salen del borrador para evitar duplicados si se repite la acción
        const okIds = new Set(data.resultados.filter((r) => r.ok).map((r) => r.id));
        const lineasOk = san.grupos.filter((g) => okIds.has(g.id)).flatMap((g) => g.paradas.map((p) => p.linea_id));
        setLineasEnrutadas((arr) => [...arr, ...lineasOk]);
        setGrupos((gs) => gs.filter((g) => !okIds.has(g.id)));
      }
      // Race condition: alguna línea entró en otra ruta desde que se seleccionó → sale del borrador, el resto se puede reintentar
      const enRuta = data.resultados.filter((r) => !r.ok && r.code === "en_ruta").flatMap((r) => (r.lineas || []).map((l) => l.linea_id || l));
      if (enRuta.length) {
        setGrupos((gs) => gs.map((g) => recomputeGrupo({ ...g, paradas: g.paradas.filter((p) => !enRuta.includes(p.linea_id)) }, inicio)));
        setIdemKey(null);
        toast.error(`${enRuta.length} línea(s) ya estaban en otra ruta: se han quitado del borrador. Revisa y vuelve a crear.`, { duration: 10000 });
      }
    } catch (err) { toast.error(err.response?.data?.detail?.msg || err.response?.data?.detail || "Error creando rutas"); }
    finally { setLoading(false); creandoRef.current = false; }
  };

  const abrirCrear = () => {
    setCrearRes(null); setIdemKey(null);
    if (manual && paradasConFechaDistinta(grupos, fechaRuta).length) { setFechasOpen(true); return; }
    setCrearOpen(true);
  };
  // Decisión explícita: reprogramar las líneas con fecha ≠ día de ruta usando el MISMO endpoint/auditoría del calendario (citas conservadas)
  const reprogramarYCrear = async () => {
    const paradas = paradasConFechaDistinta(grupos, fechaRuta);
    setLoading(true);
    try {
      for (const p of paradas) {
        await api.patch(`/calendario/montajes/${p.linea_id}/fecha`, { fecha_programada: fechaRuta, confirmar_cita: !!(p.fecha_cita && p.fecha_cita !== fechaRuta) });
      }
      setGrupos((gs) => gs.map((g) => ({ ...g, paradas: g.paradas.map((p) => paradas.some((x) => x.linea_id === p.linea_id) ? { ...p, fecha_programada: fechaRuta, fecha_montaje: fechaRuta, fecha_origen: "fecha_programada" } : p) })));
      toast.success(`${paradas.length} línea(s) reprogramadas al ${fechaRuta} (citas conservadas)`);
      setFechasOpen(false); setCrearOpen(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.msg || d || "No se pudo reprogramar; no se ha creado ninguna ruta", { duration: 9000 });
    } finally { setLoading(false); }
  };
  const resumenCrear = useMemo(() => grupos ? resumenCreacion(grupos, excluidas, fechaRuta) : null, [grupos, excluidas, fechaRuta]);
  const cap = grupos?.[0]?.capacidad ?? (f.max_paradas === "" ? null : Number(f.max_paradas));

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader title="Reparto inteligente" subtitle={manual ? "Selección manual desde el calendario · agrupa, ordena y crea la ruta con las líneas elegidas" : "Planificador por capacidad, geografía y elegibilidad · la sugerencia es un borrador editable; nada se crea hasta que confirmes"} />

      {manual ? (
        <SeleccionManualBanner manual={manual} resumen={analisis?.resumen} noElegibles={manualInfo?.no_elegibles} fechas={manualInfo?.fechas}
          onVolver={() => volverAlCalendario(lineasEnrutadas)} onReanalizar={() => cargarSeleccion(manual)} loading={loading} />
      ) : !opciones ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-amber-400" /></div>
      ) : (
        <PanelFiltros opciones={opciones} f={f} setF={setF} onAnalizar={analizar} loading={loading} />
      )}

      <div className="mt-5 space-y-5">
        {loading && <div className="text-center py-6"><Loader2 className="w-8 h-8 animate-spin inline text-amber-400" /></div>}
        {analisis && !manual && <ResumenAnalisis resumen={analisis.resumen} excluidos={analisis.excluidos_detalle} generado={!!grupos} />}
        {analisis && !loading && !manual && <DiaRutaSelector f={f} setF={setF} resumen={analisis.resumen} onGenerar={generar} />}

        {!analisis && opciones && !loading && !manual && (
          <div className="text-center py-14 bg-zinc-900/30 rounded-xl border border-dashed border-zinc-800">
            <Sparkles className="w-10 h-10 text-amber-500/30 mx-auto mb-3" />
            <div className="text-sm text-zinc-400">Ajusta el ámbito, los trabajos y la capacidad, pulsa <b>Analizar periodo</b>, elige el día de la ruta y después <b>Generar sugerencia</b>. Podrás mover, fijar y excluir paradas antes de crear las rutas.</div>
          </div>
        )}

        {grupos && (
          <>
            {/* Barra de acciones del borrador */}
            <div className="flex flex-wrap items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-xl p-3" data-testid="barra-borrador">
              <div className="text-xs text-zinc-400 mr-auto">
                <b className="text-zinc-200">Borrador:</b> {grupos.length} ruta(s) · {grupos.reduce((a, g) => a + g.paradas.length, 0)} paradas
                {excluidas.length > 0 && <> · <span className="text-zinc-300">{excluidas.length} excluidas</span></>}
                {grupos.some((g) => g.paradas.some((p) => p.fijada)) && <> · 🔒 {grupos.reduce((a, g) => a + g.paradas.filter((p) => p.fijada).length, 0)} fijadas</>}
              </div>
              <Button size="sm" variant="outline" onClick={deshacer} disabled={!historial.length || loading} data-testid="deshacer-btn" className="bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"><Undo2 className="w-3.5 h-3.5 mr-1" /> Deshacer</Button>
              <Button size="sm" variant="outline" onClick={() => aplicar(anadirGrupoVacio(grupos, cap), null)} disabled={loading} data-testid="anadir-ruta-btn" className="bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Añadir ruta vacía</Button>
              <Button size="sm" variant="outline" onClick={() => aplicar(limpiarVacios(grupos), null)} disabled={loading || !grupos.some((g) => !g.paradas.length)} data-testid="quitar-vacias-btn" className="bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Quitar vacías</Button>
              <Button size="sm" variant="outline" onClick={reoptimizar} disabled={loading || grupos.length === 0} data-testid="reoptimizar-btn" className="bg-transparent border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/10 h-8 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Reoptimizar</Button>
              <Button size="sm" onClick={abrirCrear} disabled={loading || !grupos.some((g) => g.paradas.length)} data-testid="crear-todas-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold h-8 text-xs"><Layers className="w-3.5 h-3.5 mr-1" /> Crear todas las rutas</Button>
            </div>

            <MapaPropuesta grupos={grupos} excluidas={excluidas} inicio={inicio} />

            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="grupos-list">
                {grupos.map((g, i) => (
                  <GrupoCard key={g.id} g={g} i={i} grupos={grupos} fechaPlan={fechaRuta} montadores={montadores} onMontador={onMontador}
                    onOportunidades={setOportunidadesId} acciones={{ onMover, onFijar, onExcluir }} />
                ))}
              </div>
            </DndContext>

            {excluidas.length > 0 && (
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3" data-testid="excluidas-propuesta">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Excluidas de esta propuesta ({excluidas.length}) · siguen pendientes en la app</div>
                <ul className="space-y-1.5 text-xs">
                  {excluidas.map((p) => (
                    <li key={p.linea_id} className="flex flex-wrap items-center gap-2 bg-zinc-950/50 rounded px-2 py-1.5" data-testid={`excluida-${p.linea_id}`}>
                      <span className="font-semibold text-zinc-200">{p.espacio_nombre}</span>
                      <span className="text-zinc-500">{p.ciudad} · {p.peticion_codigo}{p.marca ? ` · ${p.marca}` : ""}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/30">{p.motivo}</span>
                      <div className="ml-auto flex gap-1">
                        {grupos.map((g) => (
                          <button key={g.id} type="button" onClick={() => onRestaurar(p.linea_id, g.id)} data-testid={`restaurar-${p.linea_id}-${g.id}`}
                            disabled={g.capacidad && g.paradas.length >= g.capacidad}
                            className="text-[10px] px-2 py-0.5 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 disabled:opacity-40">→ {g.id}</button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <CrearTodasDialog open={crearOpen} onOpenChange={setCrearOpen} resumen={resumenCrear} onConfirm={crearTodas} resultado={crearRes} loading={loading}
        onVolver={manual ? () => volverAlCalendario(lineasEnrutadas) : undefined} />
      {manual && grupos && (
        <FechasRutaDialog open={fechasOpen} onOpenChange={setFechasOpen} paradas={paradasConFechaDistinta(grupos, fechaRuta)} fechaRuta={fechaRuta} loading={loading}
          onCrearSinCambiar={() => { setFechasOpen(false); setCrearOpen(true); }} onReprogramarYCrear={reprogramarYCrear} />
      )}
      {grupos && (
        <OportunidadesDialog open={!!oportunidadesId} onOpenChange={(o) => !o && setOportunidadesId(null)}
          grupo={grupos.find((g) => g.id === oportunidadesId) || null} grupos={grupos} excluidas={excluidas}
          filtrosPayload={aPayload(f)} inicio={inicio} onAnadir={onAnadirOportunidades} />
      )}
    </div>
  );
}
